import { Children, cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useApp } from '../../../app/AppContext.jsx'

import { useBottomSheetGestures } from '../../hooks/useBottomSheetGestures.js'

import { Button } from '../Button/index.js'
import { ChevronDown, ChevronUp, Close }  from '../icons/index.js'

import styles from './BottomSheet.module.scss'

// ── Slot sub-components ────────────────────────────────────────────────────────

function Header({
  children,
  onClose,
  closeLabel = 'Close',
  onToggleMinimize,
  toggleMinimizeLabel,
  isMinimized = false,
  showCloseButton = true,
  showMinimizeButton = false,
  className,
  ...rest
}) {
  const headerClassName = [styles.headerArea, className].filter(Boolean).join(' ')

  return (
    <div className={headerClassName} {...rest}>
      <div className={styles.title}>{children}</div>
      <div className={styles.actions}>
        {showMinimizeButton && onToggleMinimize && (
          <Button
            type="button"
            kind="icon"
            size="small"
            variant="ghost"
            onClick={onToggleMinimize}
            aria-label={toggleMinimizeLabel}
          >
            {isMinimized ? <ChevronUp /> : <ChevronDown />}
          </Button>
        )}

        {showCloseButton && onClose && (
          <Button type="button" kind="icon" size="small" variant="ghost" onClick={onClose} aria-label={closeLabel}>
            <Close />
          </Button>
        )}
      </div>
    </div>
  )
}
Header.displayName = 'BottomSheet.Header'

function Content({ children, className }) {
  const cls = [styles.contentArea, className].filter(Boolean).join(' ')
  return <div className={cls}>{children}</div>
}
Content.displayName = 'BottomSheet.Content'

function Footer({ children }) {
  return <div className={styles.footerArea}>{children}</div>
}
Footer.displayName = 'BottomSheet.Footer'

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * BottomSheet — slide-up mobile panel.
 *
 * Two modes controlled by `blocking`:
 * - `blocking` (default, e.g. filters): backdrop overlay, backdrop click closes.
 * - Non-blocking (e.g. product preview): no backdrop, user can interact with
 *   content behind the sheet.
 *
 * Slot API: `BottomSheet.Header`, `BottomSheet.Content`, `BottomSheet.Footer`.
 * All slots are optional.
 *
 * @param {object}    props
 * @param {boolean}   props.isOpen
 * @param {() => void} props.onClose
 * @param {boolean}   [props.blocking=true]  - Show backdrop; backdrop click closes
 * @param {string}    [props.ariaLabel]
 * @param {string}    [props.className]       - Extra class on the sheet root
 * @param {string | number} [props.bottomInset] - CSS bottom inset for keeping sheet above mobile nav
 * @param {'default' | 'offwhite'} [props.surface='default'] - Optional sheet surface variant
 * @param {React.ReactNode} props.children
 */
export function BottomSheet({
  isOpen,
  onClose,
  closeLabel = 'Close',
  minimizeLabel = 'Minimize',
  expandLabel = 'Expand',
  showCloseButton = true,
  showMinimizeButton,
  blocking = false,
  ariaLabel,
  className,
  bottomInset,
  surface = 'default',
  isMinimized = false,
  onExpand,
  onMinimize,
  children,
}) {
  const { portalHostId } = useApp()

  const CLOSE_ANIM_MS = 220
  const [isRendered, setIsRendered] = useState(isOpen)
  const [sheetState, setSheetState] = useState(isOpen ? 'opening' : 'closed')
  const [dragOffset, setDragOffset] = useState(0)
  const closeTimerRef = useRef(null)
  const openTimerRef = useRef(null)
  const sheetRef = useRef(null)

  const {
    onHeaderTouchStart,
    onHeaderTouchMove,
    onHeaderTouchEnd,
    onHeaderPointerDown,
    onHeaderPointerMove,
    onHeaderPointerEnd,
    onHeaderPointerCancel,
  } = useBottomSheetGestures({
    isMinimized,
    onExpand,
    onMinimize,
    onClose,
    onHeaderSwipeMove: setDragOffset,
    onHeaderSwipeEnd: () => setDragOffset(0),
  })

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }

    if (isOpen) {
      setIsRendered(true)
      setSheetState('opening')
      setDragOffset(0)
      const rafId = window.requestAnimationFrame(() => {
        openTimerRef.current = window.setTimeout(() => {
          setSheetState('open')
          openTimerRef.current = null
        }, 16)
      })
      return () => {
        window.cancelAnimationFrame(rafId)
        if (openTimerRef.current) {
          clearTimeout(openTimerRef.current)
          openTimerRef.current = null
        }
      }
    }

    if (!isRendered) return undefined

    setDragOffset(0)
    setSheetState('closing')
    closeTimerRef.current = window.setTimeout(() => {
      setIsRendered(false)
      setSheetState('closed')
      closeTimerRef.current = null
    }, CLOSE_ANIM_MS)

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [isOpen, isRendered])

  // Lock host page scroll whenever a sheet is open.
  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    const prevOverscrollBehavior = document.body.style.overscrollBehavior
    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'none'
    return () => {
      document.body.style.overflow = prevOverflow
      document.body.style.overscrollBehavior = prevOverscrollBehavior
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    const handler = (event) => {
      if (!blocking) return
      if (!sheetRef.current) return
      if (sheetRef.current.contains(event.target)) return
      onClose?.()
    }

    document.addEventListener('pointerdown', handler, true)
    return () => document.removeEventListener('pointerdown', handler, true)
  }, [blocking, isOpen, onClose])

  const composeTouchHandler = (ours, theirs) => (event) => {
    ours?.(event)
    theirs?.(event)
  }

  // Keep sheet interactions isolated from parent view gestures (e.g. Hero model drag).
  const stopSheetEventPropagation = (event) => {
    event.stopPropagation()
  }

  const canToggleMinimize = Boolean(onMinimize || onExpand)
  const resolvedShowMinimizeButton = showMinimizeButton ?? canToggleMinimize
  const handleToggleMinimize = () => {
    if (isMinimized) onExpand?.()
    else onMinimize?.()
  }

  const renderedChildren = useMemo(() => Children.map(children, (child) => {
    if (!isValidElement(child)) return child
    if (child.type !== Header) return child

    const resolvedOnClose = child.props.onClose ?? onClose
    const resolvedCloseLabel = child.props.closeLabel ?? closeLabel

    return cloneElement(child, {
      onClose: resolvedOnClose,
      closeLabel: resolvedCloseLabel,
      showCloseButton: child.props.showCloseButton ?? showCloseButton,
      showMinimizeButton: child.props.showMinimizeButton ?? resolvedShowMinimizeButton,
      onToggleMinimize: child.props.onToggleMinimize ?? handleToggleMinimize,
      toggleMinimizeLabel: child.props.toggleMinimizeLabel ?? (isMinimized ? expandLabel : minimizeLabel),
      isMinimized: child.props.isMinimized ?? isMinimized,
      onTouchStart: composeTouchHandler(onHeaderTouchStart, child.props.onTouchStart),
      onTouchMove: composeTouchHandler(onHeaderTouchMove, child.props.onTouchMove),
      onTouchEnd: composeTouchHandler(onHeaderTouchEnd, child.props.onTouchEnd),
      onPointerDown: composeTouchHandler(onHeaderPointerDown, child.props.onPointerDown),
      onPointerMove: composeTouchHandler(onHeaderPointerMove, child.props.onPointerMove),
      onPointerUp: composeTouchHandler(onHeaderPointerEnd, child.props.onPointerUp),
      onPointerCancel: composeTouchHandler(onHeaderPointerCancel, child.props.onPointerCancel),
    })
  }), [
    closeLabel,
    children,
    expandLabel,
    isMinimized,
    minimizeLabel,
    onClose,
    resolvedShowMinimizeButton,
    showCloseButton,
    onHeaderPointerCancel,
    onHeaderPointerDown,
    onHeaderPointerEnd,
    onHeaderPointerMove,
    onHeaderTouchEnd,
    onHeaderTouchMove,
    onHeaderTouchStart,
  ])

  if (!isRendered) return null

  const portalTarget = document.getElementById(portalHostId)
  if (!portalTarget) return null

  const sheetClass = [
    styles.sheet,
    !blocking && styles.sheetNonBlocking,
    surface === 'offwhite' && styles.sheetSurfaceOffwhite,
    className,
  ].filter(Boolean).join(' ')

  const sheetStyle = bottomInset == null
    ? { '--bim-bottom-sheet-translate-y': `${dragOffset}px` }
    : {
      '--bim-bottom-sheet-inset': typeof bottomInset === 'number' ? `${bottomInset}px` : bottomInset,
      '--bim-bottom-sheet-translate-y': `${dragOffset}px`,
    }

  return createPortal(
    <>
      {blocking && (
        <div
          className={styles.backdrop}
          onClick={onClose}
          aria-hidden="true"
          data-state={sheetState}
        />
      )}

      <div
        ref={sheetRef}
        role="dialog"
        aria-modal={blocking ? 'true' : undefined}
        aria-label={ariaLabel}
        className={sheetClass}
        style={sheetStyle}
        data-state={sheetState}
        onPointerDown={stopSheetEventPropagation}
        onPointerMove={stopSheetEventPropagation}
        onPointerUp={stopSheetEventPropagation}
        onMouseDown={stopSheetEventPropagation}
        onMouseMove={stopSheetEventPropagation}
        onMouseUp={stopSheetEventPropagation}
        onTouchStart={stopSheetEventPropagation}
        onTouchMove={stopSheetEventPropagation}
        onTouchEnd={stopSheetEventPropagation}
      >
        {renderedChildren}
      </div>
    </>,
    portalTarget,
  )
}

BottomSheet.Header  = Header
BottomSheet.Content = Content
BottomSheet.Footer  = Footer
